import { useEffect } from 'react'
import api from '../api/api'

const sessionEvents = new Set()
const sessionStorageKey = 'eco_analytics_session_id'

const getSessionId = () => {
  if (typeof window === 'undefined') return ''
  const existing = window.localStorage.getItem(sessionStorageKey)
  if (existing) return existing
  const generated = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  window.localStorage.setItem(sessionStorageKey, generated)
  return generated
}

const sourceTypeMap = {
  home: ['home_featured', 'anasayfa_vitrini', 'weekly_deal', 'daily_deal', 'gunun_firsati'],
  home_featured: ['home_featured', 'anasayfa_vitrini'],
  weekly_deal: ['weekly_deal'],
  daily_deal: ['daily_deal', 'gunun_firsati'],
  market: ['marketplace_featured', 'home_featured', 'category_featured', 'search_featured', 'weekly_deal', 'daily_deal'],
  marketplace: ['marketplace_featured'],
  category: ['category_featured'],
  search: ['search_featured', 'arama_vitrini'],
  vitrine: ['home_featured', 'anasayfa_vitrini', 'marketplace_featured', 'category_featured', 'search_featured', 'arama_vitrini', 'weekly_deal'],
  vitrin_page: ['home_featured', 'anasayfa_vitrini', 'marketplace_featured', 'category_featured', 'search_featured', 'arama_vitrini', 'weekly_deal'],
  weekly_page: ['weekly_deal'],
  seller_shop: ['marketplace_featured', 'home_featured', 'category_featured', 'search_featured', 'weekly_deal', 'daily_deal'],
  shop: ['marketplace_featured', 'home_featured', 'category_featured', 'search_featured', 'weekly_deal', 'daily_deal']
}

export const sourceFromPath = (pathname = '', search = '') => {
  // Phase 4H: /bazar?type= ilə source tracking
  const params = new URLSearchParams(search)
  const bazarType = params.get('type')
  if (pathname.includes('/bazar') && bazarType) {
    if (bazarType === 'vitrin') return 'vitrine'
    if (bazarType === 'weekly') return 'weekly_page'
    if (bazarType === 'bumped') return 'market'
    if (bazarType === 'discount') return 'market'
  }
  // Legacy path-based fallback (redirect may still show old path briefly)
  if (pathname.includes('vitrin-mehsullari')) return 'vitrine'
  if (pathname.includes('haftanin-firsatlari')) return 'weekly_page'
  if (pathname.includes('category')) return 'category'
  if (pathname.includes('search') || search) return 'search'
  if (pathname.includes('shops')) return 'seller_shop'
  if (pathname.includes('market') || pathname.includes('bazar')) return 'market'
  return 'market'
}

export const resolveDopingBoost = (product = {}, source = '', fallbackItem = null) => {
  const details = product.activeBoostDetails || product.activeDopingDetails || []
  const allowedTypes = sourceTypeMap[source] || []
  const match = details.find(boost => allowedTypes.includes(boost.type || boost.dopingType))
    || details[0]

  if (match) {
    return {
      id: match._id || match.dopingPurchaseId || match.id,
      type: match.type || match.dopingType
    }
  }

  if (fallbackItem?._id && (fallbackItem.dopingType || fallbackItem.type)) {
    return {
      id: fallbackItem._id,
      type: fallbackItem.dopingType || fallbackItem.type
    }
  }

  const boosts = product.activeBoosts || (product.doping?.isActive ? [product.doping.dopingType] : [])
  const type = boosts.find(item => allowedTypes.includes(item)) || boosts[0]
  return type ? { id: product.dopingId || product.doping?._id || null, type } : null
}

const sendTracking = (payload) => {
  if (!payload?.productId || !payload?.eventType) return

  api.post('/home/doping/track', { ...payload, sessionId: getSessionId() }).catch(() => {})
}

export const trackDopingEvent = ({ product, eventType, source, boost }) => {
  const resolvedBoost = boost || resolveDopingBoost(product, source)
  if (!product?._id || !resolvedBoost?.type) return

  const key = `${eventType}:${product._id}:${resolvedBoost.id || resolvedBoost.type}:${source}`
  if (sessionEvents.has(key)) return
  sessionEvents.add(key)

  sendTracking({
    productId: product._id,
    dopingPurchaseId: resolvedBoost.id || undefined,
    eventType,
    source
  })
}

export const trackDopingClick = (product, source, boost) => {
  trackDopingEvent({ product, eventType: 'click', source, boost })
}

export const trackProductView = (productId, source = 'product_detail') => {
  if (!productId) return
  const key = `product-view:${productId}:${source}`
  if (sessionEvents.has(key)) return
  sessionEvents.add(key)

  sendTracking({
    productId,
    eventType: 'product_view',
    source,
  })
}

export const useDopingImpression = ({ ref, product, source, boost }) => {
  useEffect(() => {
    const element = ref?.current
    const resolvedBoost = boost || resolveDopingBoost(product, source)
    if (!element || !product?._id || !resolvedBoost?.type || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(entries => {
      const entry = entries[0]
      if (!entry?.isIntersecting) return
      trackDopingEvent({ product, eventType: 'impression', source, boost: resolvedBoost })
      observer.disconnect()
    }, { threshold: 0.45 })

    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, product, source, boost])
}
